import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import moment from 'moment';
import styled from 'styled-components';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';
import { IMaskInput } from 'react-imask';
import { NumericFormat } from 'react-number-format';
import Button from '@mui/material/Button';
import SelectEventDateForm from './SelectEventDateForm';
import { FormControl, InputLabel, Select, MenuItem, Autocomplete, Box, Grid } from '@mui/material';
import { message } from 'antd';
import 'react-phone-number-input/style.css';
import '../../../Styles/Interface/Events/EventForm.css';

import MaterialPhoneInput from '../utils/MaterialPhoneInput'

const ErrorMessageStyled = styled.span`
  color: red;
  font-size: 14px;
  margin: 0;
  padding: 0;
  marginBottom: 25px;
  marginTop: 2px;
`;

const TextMaskCustom = React.forwardRef(function TextMaskCustom(props, ref) {
  const { onChange, values, handleChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask="#00-000-0000"
      definitions={{
        '#': /[8]/,
      }}
      inputRef={ref}
      onAccept={(value) => onChange({ target: { name: props.name, value } })}
      overwrite
    />
  );
});

TextMaskCustom.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const NumericFormatCustom = React.forwardRef(
  function NumericFormatCustom(props, ref) {
    const { onChange, values, handleChange, ...other } = props;

    return (
      <NumericFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: values.value,
            },
          });
        }}
        thousandSeparator
        valueIsNumericString
        prefix="RD$"
      />
    );
  },
);

NumericFormatCustom.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
};

const safeParseFloat = (value, defaultValue = 0) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value.replace(/,/g, ''));
    return isNaN(parsed) ? defaultValue : parsed;
  }
  return defaultValue;
};

const formatCurrency = (value) => {
  return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const EventFormBoda = forwardRef((props, ref) => {
  const { handleFormValues, formValues, nextStepHandler, reservationStep, clients, dressList, getDressListHandler,
    eventsScheduledXV, dressReservations, suitList, getSuitListHandler, suitReservations, requestClients } = props;
  const formRef = useRef(null);
  const [eventDate, setEventDate] = useState(null);
  const [longDressList, setLongDressList] = useState([]);
  const [availableLongDresses, setAvailableLongDresses] = useState([]);
  const [availableSuits, setAvailableSuits] = useState([]);
  const [clientType, setClientType] = useState('new');
  const [clientsList, setClientsList] = useState([]);
  const [messageApi, contextHolder] = message.useMessage();

  useImperativeHandle(ref, () => ({
    handleSubmit: () => {
      if (formRef.current) {
        formRef.current.submitForm()
      }
    }
  }));

  const handleEventDate = (date) => {
    setEventDate(date)
  };

  const handleClientTypeChange = (event) => {
    setClientType(event.target.value);
  };

  const filterAvailableDresses = (dressList) => {
    return dressList.filter(dress => {
      return !dressReservations.some(reservation => {
        const reservationStart = new Date(reservation.eventDateStart);
        const reservationEnd = new Date(reservation.eventDateEnd);
        const eventStart = new Date(eventDate.start);
        const eventEnd = new Date(eventDate.end);
        return (
          reservation.dressID === dress.dressID &&
          ((eventStart >= reservationStart && eventStart <= reservationEnd) ||
            (eventEnd >= reservationStart && eventEnd <= reservationEnd) ||
            (eventStart <= reservationStart && eventEnd >= reservationEnd))
        );
      });
    });
  };

  const filterAvailableSuit = (suits) => {
    return suits.filter(suit => {
      return !suitReservations.some(reservation => {
        const reservationStart = new Date(reservation.eventDateStart);
        const reservationEnd = new Date(reservation.eventDateEnd);
        const eventStart = new Date(eventDate.start);
        const eventEnd = new Date(eventDate.end);
        return (
          reservation.suitID === suit.suitID &&
          ((eventStart >= reservationStart && eventStart <= reservationEnd) ||
            (eventEnd >= reservationStart && eventEnd <= reservationEnd) ||
            (eventStart <= reservationStart && eventEnd >= reservationEnd))
        );
      });
    });
  };

  const handleChangeLongDress = (dress) => {
    let longDress = [];
    for (let i = 0; i < dress.length; i++) {
      if (dress[i].dressSize === 'long' && dress[i].dressType === 'wedding') {
        longDress.push(dress[i])
      }
    }
    setLongDressList([...longDress])
  };

  const handleReservationInf = async (values) => {
    const formValues = {
      client: {
        clientName: values.clientName,
        phoneNumber: values.clientPhone,
        cellPhoneNumber: values.clientCellphone,
        clientEmail: values.clientEmail,
        address: values.address1,
        clientID: values.clientID
      },
      couple: {
        name: values.coupleName,
        cellphoneNumber: values.coupleCellphone,
        email: values.coupleEmail
      },
      event: {
        eventDateStart: moment(eventDate.start).format('YYYY-MM-DD HH:mm:ss'),
        eventDateEnd: moment(eventDate.end).format('YYYY-MM-DD HH:mm:ss'),
        eventType: values.eventType,
        eventLocation: Object.keys(values.eventPlace).length > 0 ? values.eventPlace : 'Sesión en estudio.',
        eventTitle: `Boda de ${values.clientName} y ${values.coupleName}`,
        eventDescription: `${values.comboName} - ${values.eventPlace}`,
        status: 'pendiente',
        paymentStatus: 'pendiente',
      },
      reservation: {
        comboName: values.comboName,
        comboPrice: values.comboPrice,
        longDress: values.longDress,
        longDressID: values.longDressID,
        suitID: values.suitID,
        suit: values.suit,
        deposit: 2000,
        paymentStatus: 'pendiente',
        referralSource: values.referralSource,
        discount: values.discount,
        notes: values.notes
      },
      dressAgenda: {
        eventDateStart: moment(eventDate.start).format('YYYY-MM-DD HH:mm:ss'),
        eventDateEnd: moment(eventDate.end).format('YYYY-MM-DD HH:mm:ss'),
        longDressID: values.longDressID,
        status: 'pendiente'
      },
      suitAgenda: {
        eventDateStart: moment(eventDate.start).format('YYYY-MM-DD HH:mm:ss'),
        eventDateEnd: moment(eventDate.end).format('YYYY-MM-DD HH:mm:ss'),
        suitID: values.suitID,
        status: 'pending'
      },
      reservationCashFlow: {
        paymentMethod: 'efectivo',
        amount: 2000,
        description: `Deposito por la reservación de (${values.comboName}) - ${Object.keys(values.eventPlace).length > 0 ? values.eventPlace : 'Sesión en estudio.'}`,
        ServiceID: 24,
        flowType: 'ingreso'
      },
      reservationServices: {
        serviceQty: 1,
        serviceID: 24,
        discount: 0,
        status: 'pendiente'
      }
    }
    console.log(formValues);
    handleFormValues(formValues);
    nextStepHandler();
  };

  const handleFormSubmit = async (values) => {
    return await handleReservationInf(values);
  };

  useEffect(() => {
    window.onbeforeunload = () => {
      window.scrollTo(0, 0);
    };
  }, []);

  useEffect(() => {
    console.log(clients, 'Clients');
    if (clients && clients.length > 0) {
      const options = clients.map((client) => ({
        label: `${client.clientName} - ${client.cellphoneNumber}`,
        value: client
      }));
      setClientsList([...options]);
    }
  }, []);

  useEffect(() => {
    if (clients && clients.length > 0) {
      const options = clients.map((client) => ({
        label: `${client.clientName} - ${client.cellphoneNumber}`,
        value: client
      }));
      setClientsList([...options]);
    } else {
      requestClients();
    }
  }, [clientType]);

  useEffect(() => {
    if (dressList.length === 0) {
      getDressListHandler();
    }
    else {
      handleChangeLongDress(dressList);
    }
  }, []);
  useEffect(() => {
    if (dressList.length === 0) {
      getDressListHandler();
    }
    else {
      handleChangeLongDress(dressList);
    }
  }, [dressList]);

  useEffect(() => {
    if (suitList.length === 0) {
      getSuitListHandler();
    }
  }, []);
  useEffect(() => {
    if (suitList.length === 0) {
      getSuitListHandler();
    }
  }, [suitList]);

  useEffect(() => {
    if (eventDate) {
      setAvailableLongDresses(filterAvailableDresses(longDressList));
      setAvailableSuits(filterAvailableSuit(suitList));
    }
  }, [eventDate]);

  useEffect(() => {
    if (formValues?.client?.clientID) {
      setClientType('existing');
    }
  }, []);

  return (
    <Box className="event-form-reservation-main-container" sx={{
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      background: { xs: '#fff', md: '#f8f8fc' }
    }}>
      <Box id='title-container' sx={{
        width: '100%',
        maxWidth: 700,
        mx: 'auto',
        mb: { xs: 2, md: 4 },
        textAlign: 'center'
      }}>
        <h1 style={{ fontSize: '2.5rem', fontWeight: 700, marginBottom: 0 }}>Formulario de reservación de bodas</h1>
      </Box>
      <Box id="main-container" sx={{
        width: '100%',
        maxWidth: 700,
        mx: 'auto',
        bgcolor: 'background.paper',
        borderRadius: { xs: 0, md: 3 },
        boxShadow: { xs: 'none', md: '0 4px 24px rgba(83,29,171,0.08)' },
        p: { xs: 1, sm: 2, md: 4 },
        mb: 2
      }}>
        <Box id="user-main-container" sx={{ width: '100%' }}>
          <Formik
            initialValues={{
              clientName: Object.keys(formValues).length > 0 ? formValues.client.clientName : '',
              clientPhone: Object.keys(formValues).length > 0 ? formValues.client.phoneNumber : '',
              clientCellphone: Object.keys(formValues).length > 0 ? formValues.client.cellPhoneNumber : '',
              clientEmail: Object.keys(formValues).length > 0 ? formValues.client.clientEmail : '',
              address1: Object.keys(formValues).length > 0 ? formValues.client.address : '',
              coupleName: Object.keys(formValues).length > 0 ? formValues.couple.name : '',
              coupleCellphone: Object.keys(formValues).length > 0 ? formValues.couple.cellphoneNumber : '',
              coupleEmail: Object.keys(formValues).length > 0 ? formValues.couple.email : '',
              eventDate: eventDate,
              shortDress: Object.keys(formValues).length > 0 ? formValues.reservation.shortDress : '',
              shortDressID: Object.keys(formValues).length > 0 ? formValues.reservation.shortDressID : '',
              longDress: Object.keys(formValues).length > 0 ? formValues.reservation.longDress : '',
              longDressID: Object.keys(formValues).length > 0 ? formValues.reservation.longDressID : '',
              suit: Object.keys(formValues).length > 0 ? formValues.reservation.suit : '',
              suitID: Object.keys(formValues).length > 0 ? formValues.reservation.suitID : '',
              referralSource: Object.keys(formValues).length > 0 ? formValues.reservation.referralSource : '',
              comboName: Object.keys(formValues).length > 0 ? formValues.reservation.comboName : '',
              comboPrice: Object.keys(formValues).length > 0 ? formValues.reservation.comboPrice : '',
              discount: Object.keys(formValues).length > 0 ? formValues.reservation.discount : 0,
              reservationPayment: Object.keys(formValues).length > 0 ? formValues.reservation.deposit : '',
              eventType: Object.keys(formValues).length > 0 ? formValues.event.eventType : null,
              eventPlace: Object.keys(formValues).length > 0 ? formValues.event.eventLocation : '',
              notes: Object.keys(formValues).length > 0 ? formValues.reservation.notes : '',
            }}

            onSubmit={(values, { setSubmitting }) => {
              handleFormSubmit(values)
              setSubmitting(false)
            }}
            innerRef={formRef}
            validate={values => {
              const errors = {};
              if (!values.clientName) {
                errors.clientName = '*El nombre es obligatorio';
              }
              if (!values.clientEmail) {
                errors.clientEmail = '*Requerido';
              } else if (
                !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.clientEmail)
              ) {
                errors.clientEmail = 'Correo electrónico invalido';
              }
              if (!values.coupleName) {
                errors.coupleName = '*El nombre es obligatorio'
              }
              if (!values.coupleEmail) {
                errors.coupleEmail = '*Requerido'
              }
              if (!values.clientCellphone) {
                errors.clientCellphone = '*Requerido'
              }
              if (values.clientCellphone.length < 10) {
                errors.clientCellphone = 'Por favor introduce un número de celular valido'
              }
              if (values.clientCellphone === '') {
                errors.clientCellphone = '*Requerido'
              }
              if (values.comboPrice === 0) {
                errors.comboPrice = '*Requerido'
              }
              if (values.comboPrice < 6000) {
                errors.comboPrice = 'Introduce un precio valido.'
              }
              if (!eventDate) {
                errors.eventDate = 'Seleccione la fecha del evento'
              }
              if (Object.keys(errors).length > 0) {
                errors.isSubmitting = 'Debes llenar todos los campos requeridos en el formulario.'
              }
              if (values.eventType === null || values.eventType.length === 0) {
                errors.eventType = 'Debes seleccionar el tipo de evento para poder agendar.'
              }
              if (values.longDress === null || values.longDress.length === 0 || values.longDress === '') {
                errors.longDress = 'Debes seleccionar un vestido de novia.'
              }
              return errors;
            }}
          >
            {({
              isSubmitting,
              errors,
              touched,
              setFieldValue,
              handleSubmit,
              handleChange,
              values
            }) => (
              <Form className='event-form-reservation' style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '1.5rem'
              }}>
                {contextHolder}
                <fieldset className='reservation-fieldset' style={{
                  borderRadius: 12,
                  border: '1px solid #eee',
                  padding: '1rem',
                  marginBottom: '1.5rem',
                  boxShadow: '0 2px 8px rgba(83,29,171,0.04)'
                }}>
                  <legend className='reservation-legend' style={{ fontWeight: 700, fontSize: '1.2rem' }}><h2 style={{ margin: 0 }}>Información del cliente</h2></legend>
                  <Box className='client-information-container' sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    flexWrap: 'wrap',
                    gap: { xs: 2, sm: 3 },
                    mb: 2
                  }}>
                    <Box className='input-container column-2' sx={{ flex: 1, minWidth: { xs: '100%', sm: '45%' } }}>
                      <TextField
                        select
                        label="Tipo de Cliente"
                        value={clientType}
                        onChange={handleClientTypeChange}
                        variant="outlined"
                        className='column-2'
                      >
                        <MenuItem value="new">Nuevo Cliente</MenuItem>
                        <MenuItem value="existing" disabled={clients.length === 0}>Cliente Registrado</MenuItem>
                      </TextField>
                    </Box>
                    {clientType === 'new' && (
                      <>
                        <Box className='input-container' sx={{ width: '100%' }}>
                          <Field name="clientName">
                            {({ field }) => (
                              <TextField {...field} id="outlined-basic" label="Nombre" variant="outlined" autoComplete='name'
                                color={errors.clientName ? 'error' : !errors.clientName && touched.clientName ? 'success' : ''}
                                focused={touched.clientName}
                                placeholder={errors.clientName ? '*Requerido' : ''}
                              />
                            )}
                          </Field>
                          <ErrorMessage name='clientName' component={ErrorMessageStyled} />
                        </Box>
                        <Box className='input-container' sx={{ width: '100%' }}>
                          <Field type="email" name="clientEmail" >
                            {({ field }) => (
                              <TextField {...field} id="outlined-basic" label="Email" variant="outlined"
                                color={errors.clientEmail ? 'error' : !errors.clientEmail && touched.clientEmail ? 'success' : ''}
                                focused={touched.clientEmail}
                                placeholder={errors.clientEmail ? '*Requerido' : ''}
                              />
                            )}
                          </Field>
                          <ErrorMessage name='clientEmail' component={ErrorMessageStyled} />
                        </Box>
                        <Grid item xs={12} sm={6}>
                          <Field name='clientPhone'>
                            {({ field, form, meta }) => (
                              <MaterialPhoneInput
                                label="Número de teléfono"
                                name={field.name}
                                value={field.value}
                                // El onChange de PhoneInput devuelve el valor directamente,
                                // por lo que usamos setFieldValue para actualizar Formik.
                                onChange={(phone) => {
                                  form.setFieldValue(field.name, phone);
                                }}
                                // Pasamos el estado de error y el texto de ayuda desde Formik
                                error={meta.touched && Boolean(meta.error)}
                                helperText={meta.touched && meta.error}
                                color={meta.error ? 'error' : (!meta.error && meta.touched ? 'success' : undefined)}
                                labelFontSize={18}
                                defaultCountry="do" // Puedes pasar cualquier otra prop de PhoneInput aquí
                                Autocomplete='clientPhone'
                              />
                            )}
                          </Field>
                          <ErrorMessage name='clientPhone' component={ErrorMessageStyled} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Field name='clientCellphone'>
                            {({ field, form, meta }) => (
                              <MaterialPhoneInput
                                label="Número de celular"
                                name={field.name}
                                value={field.value}
                                // El onChange de PhoneInput devuelve el valor directamente,
                                // por lo que usamos setFieldValue para actualizar Formik.
                                onChange={(phone) => {
                                  form.setFieldValue(field.name, phone);
                                }}
                                // Pasamos el estado de error y el texto de ayuda desde Formik
                                error={meta.touched && Boolean(meta.error)}
                                helperText={meta.touched && meta.error}
                                color={meta.error ? 'error' : (!meta.error && meta.touched ? 'success' : undefined)}
                                labelFontSize={18}
                                defaultCountry="do" // Puedes pasar cualquier otra prop de PhoneInput aquí
                                Autocomplete='clientCellphone'
                              />
                            )}
                          </Field>
                          <ErrorMessage name='clientCellphone' component={ErrorMessageStyled} />
                        </Grid>
                        <Box className='input-container column-2' sx={{ width: '100%' }}>
                          <Field name="address1">
                            {({ field }) => (
                              <TextField {...field} id="outlined-basic" label="Dirección de residencia" variant="outlined" />
                            )}
                          </Field>
                          <ErrorMessage name='fatherName' component={ErrorMessageStyled} />
                        </Box>
                      </>
                    )}
                    {clientType === 'existing' && (
                      <Box className='input-container' sx={{ width: '100%' }}>
                        <Autocomplete
                          freeSolo
                          options={clientsList}
                          getOptionLabel={(option) => option.label}
                          value={
                            clientsList.find(option => option.value.clientName === values.clientName)
                            || values?.clientName || clientsList[0]?.clientName
                          }
                          renderInput={(params) => (
                            <TextField {...params} label="Nombre o celular del cliente" variant="outlined" />
                          )}
                          onChange={(event, value) => {
                            if (value && typeof value === 'object') {
                              setFieldValue('clientName', value.value.clientName);
                              setFieldValue('clientPhone', value.value.phoneNumber);
                              setFieldValue('clientCellphone', value.value.cellphoneNumber);
                              setFieldValue('clientEmail', value.value.clientEmail);
                              setFieldValue('address1', value.value.address);
                              setFieldValue('clientID', value.value.clientID);
                            } else {
                              setFieldValue('clientName', value || '');
                              setFieldValue('clientPhone', '');
                              setFieldValue('clientCellphone', '');
                              setFieldValue('clientEmail', '');
                              setFieldValue('address1', '');
                              setFieldValue('clientID', '');
                            }
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </fieldset>

                <fieldset className='reservation-fieldset' style={{
                  borderRadius: 12,
                  border: '1px solid #eee',
                  padding: '1rem',
                  marginBottom: '1.5rem',
                  boxShadow: '0 2px 8px rgba(83,29,171,0.04)'
                }}>
                  <legend className='reservation-legend' style={{ fontWeight: 700, fontSize: '1.2rem' }}><h2 style={{ margin: 0 }}>Información de la pareja</h2></legend>
                  <Box className='couple-information-container' sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    flexWrap: 'wrap',
                    gap: { xs: 2, sm: 3 },
                    mb: 2
                  }}>
                    <Box className='input-container' sx={{ flex: 1, minWidth: { xs: '100%', sm: '45%' } }}>
                      <Field name="coupleName">
                        {({ field }) => (
                          <TextField {...field} id="outlined-basic" label="Nombre de la pareja" variant="outlined"
                            color={errors.coupleName ? 'error' : !errors.coupleName && touched.coupleName ? 'success' : ''}
                            focused={touched.coupleName}
                            placeholder={errors.coupleName ? '*Requerido' : ''}
                            initialValues={formValues.length > 0 && formValues.couple.coupleName}
                          />
                        )}
                      </Field>
                      <ErrorMessage name='coupleName' component={ErrorMessageStyled} />
                    </Box>
                    <Grid item xs={12}>
                      <Field name='coupleCellphone'>
                        {({ field, form, meta }) => (
                          <MaterialPhoneInput
                            label="Número de teléfono"
                            name={field.name}
                            value={field.value}
                            // El onChange de PhoneInput devuelve el valor directamente,
                            // por lo que usamos setFieldValue para actualizar Formik.
                            onChange={(phone) => {
                              form.setFieldValue(field.name, phone);
                            }}
                            // Pasamos el estado de error y el texto de ayuda desde Formik
                            error={meta.touched && Boolean(meta.error)}
                            helperText={meta.touched && meta.error}
                            color={meta.error ? 'error' : (!meta.error && meta.touched ? 'success' : undefined)}
                            labelFontSize={18}
                            defaultCountry="do" // Puedes pasar cualquier otra prop de PhoneInput aquí
                            Autocomplete='coupleCellphone'
                          />
                        )}
                      </Field>
                      <ErrorMessage name='coupleCellphone' component={ErrorMessageStyled} />
                    </Grid>
                    <Box className='input-container' sx={{ width: '100%' }}>
                      <Field name="coupleEmail" type="email">
                        {({ field }) => (
                          <TextField {...field} id="outlined-basic" label="Email de la pareja" variant="outlined" initialValues={formValues.length > 0 && formValues.couple.coupleEmail} />
                        )}
                      </Field>
                      <ErrorMessage name='coupleEmail' component={ErrorMessageStyled} />
                    </Box>
                  </Box>
                </fieldset>

                <fieldset className='reservation-fieldset' style={{
                  borderRadius: 12,
                  border: '1px solid #eee',
                  padding: '1rem',
                  marginBottom: '1.5rem',
                  boxShadow: '0 2px 8px rgba(83,29,171,0.04)'
                }}>
                  <legend className='reservation-legend' style={{ fontWeight: 700, fontSize: '1.2rem' }}><h2 style={{ margin: 1 }}>Información del evento</h2></legend>
                  <Box className='reservation-information-container' sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    mb: 2
                  }}>
                    <Box className={`event-type-container input-container ${values.eventType === null ? 'column-2' : ''}`} sx={{ width: '100%' }}>
                      <Field name="eventType">
                        {({ field, form }) => (
                          <FormControl>
                            <InputLabel id="demo-simple-select-autowidth-label">Tipo de evento</InputLabel>
                            <Select
                              labelId="simple-select-autowidth-label"
                              id="simple-select-autowidth"
                              value={field.value || ''}
                              onChange={(event) => {
                                values.eventType = event.target.value
                                form.setFieldValue(field.name, event.target.value);
                              }}
                              label="Tipo de evento"
                            >
                              <MenuItem key='outside' value='outside'>Evento en exterior</MenuItem>
                              <MenuItem key='inside' value='inside'>Evento en estudio</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      </Field>
                      <div style={{ zIndex: 999 }}><ErrorMessage name='eventType' component={ErrorMessageStyled} /></div>
                    </Box>
                    {values.eventType === 'inside' &&
                      <Box className={`input-container event-date`} sx={{ width: '100%' }}>
                        <Field name="eventDate" type="date">
                          {({ field }) => (
                            <SelectEventDateForm {...field} labelName='Fecha del evento (Estudio)' handleEventDate={handleEventDate} eventsScheduledXV={eventsScheduledXV}
                              typeOfEvent={values.eventType} eventDate={Object.keys(formValues).length > 0 ? formValues.event.eventDateStart : eventDate} />
                          )}
                        </Field>
                        <div style={{ zIndex: 999 }}><ErrorMessage name='eventDate' component={ErrorMessageStyled} /></div>
                      </Box>
                    }
                    {values.eventType === 'outside' &&
                      <Box className={`input-container event-date`} sx={{ width: '100%' }}>
                        <Field name="eventDate" type="date">
                          {({ field }) => (
                            <SelectEventDateForm {...field} labelName='Fecha del evento en exterior' handleEventDate={handleEventDate} eventsScheduledXV={eventsScheduledXV}
                              eventDate={Object.keys(formValues).length > 0 ? formValues.event.eventDateStart : eventDate} typeOfEvent={values.eventType} />
                          )}
                        </Field>
                        <div style={{ zIndex: 999 }}><ErrorMessage name='eventDate' component={ErrorMessageStyled} /></div>
                      </Box>
                    }

                    <Box className='input-container long-dress-select-container' sx={{ width: '100%' }}>
                      <Field name="longDress">
                        {({ field, form }) => (
                          <FormControl>
                            <Autocomplete
                              disablePortal
                              options={availableLongDresses}
                              getOptionLabel={(option) => `${option.code} - ${option.dressName}`}
                              value={longDressList.find(option => `${option.code} - ${option.dressName}` === field.value) || null}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  fullWidth
                                  id="longDress"
                                  name="longDress"
                                  label="Vestido de novia"
                                  value={field.value || ''}
                                  error={form.touched.longDress && Boolean(form.errors.longDress)}
                                  margin="normal"
                                />
                              )}
                              onChange={(event, value) => {
                                form.setFieldValue(field.name, value ? `${value.code} - ${value.dressName}` : '');
                                form.setFieldValue('longDressID', value ? value.dressID : '');
                              }}
                            />
                          </FormControl>
                        )}
                      </Field>
                      <ErrorMessage name='longDress' component={ErrorMessageStyled} />
                    </Box>

                    <Box className='input-container suit-select-container' sx={{ width: '100%' }}>
                      <Field name="suit">
                        {({ field, form }) => (
                          <FormControl>
                            <Autocomplete
                              disablePortal
                              options={availableSuits}
                              getOptionLabel={(option) => `${option.code} - ${option.suitName}`}
                              value={suitList.find(option => `${option.code} - ${option.suitName}` === field.value) || null}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  fullWidth
                                  id="suit"
                                  name="suit"
                                  label="Traje del novio"
                                  value={field.value || ''}
                                  error={form.touched.suit && Boolean(form.errors.suit)}
                                  margin="normal"
                                />
                              )}
                              onChange={(event, value) => {
                                form.setFieldValue(field.name, value ? `${value.code} - ${value.suitName}` : '');
                                form.setFieldValue('suitID', value ? value.suitID : '');
                              }}
                            />
                          </FormControl>
                        )}
                      </Field>
                      <ErrorMessage name='suitID' component={ErrorMessageStyled} />
                    </Box>

                    <Box className='input-container column-2' sx={{ width: '100%' }}>
                      <Field name="eventPlace">
                        {({ field }) => (
                          <TextField {...field} id="outlined-basic" label="Lugar del evento" variant="outlined" disabled={values.eventType === 'inside'}
                            value={values.eventType === 'inside' ? 'Sesión en estudio.' : field.value} />
                        )}
                      </Field>
                    </Box>
                    <Box className='input-container column-2' sx={{ width: '100%' }}>
                      <Field name="referralSource">
                        {({ field, form }) => (
                          <FormControl>
                            <InputLabel id="demo-simple-select-autowidth-label">¿Cómo escucho de nosotros?</InputLabel>
                            <Select
                              labelId="simple-select-autowidth-label"
                              id="simple-select-autowidth"
                              value={field.value || 'fuente-desconocida'}
                              onChange={(event) => {
                                values.referralSource = event.target.value
                                form.setFieldValue(field.name, event.target.value);
                              }}
                              label="¿Cómo escucho de nosotros?"
                            >
                              <MenuItem key='instagram' value='instagram'>Instagram</MenuItem>
                              <MenuItem key='facebook' value='facebook'>Facebook</MenuItem>
                              <MenuItem key='google' value='google'>Google</MenuItem>
                              <MenuItem key='Referido' value='Referido'>Referido</MenuItem>
                              <MenuItem key='other' value='other'>Otro</MenuItem>
                              <MenuItem key='fuente-desconocida' value='fuente-desconocida'>Fuente desconocida</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      </Field>
                    </Box>
                    <Box className='input-container' sx={{ width: '100%' }}>
                      <Field name="comboName">
                        {({ field }) => (
                          <TextField {...field} id="outlined-basic" label="Nombre del Combo" variant="outlined" />
                        )}
                      </Field>
                      <ErrorMessage name='comboName' component={ErrorMessageStyled} />
                    </Box>
                    <Box className='input-container' sx={{ width: '100%' }}>
                      <Field name="comboPrice">
                        {({ field }) => (
                          <TextField
                            label="Precio del combo"
                            value={values.comboPrice}
                            onChange={handleChange}
                            name="comboPrice"
                            id="formatted-NumberFormat-input"
                            slotProps={{
                              input: {
                                inputComponent: NumericFormatCustom,
                                values: values,
                                handleChange: handleChange,
                              },
                            }}
                            variant="outlined"
                          />
                        )}
                      </Field>
                      <ErrorMessage name='comboPrice' component={ErrorMessageStyled} />
                    </Box>
                    <Grid container spacing={1} sx={{ mt: 1 }} fullWidth>
                      <Grid item xs={12} sm={6}>
                        <Field name="discount">
                          {({ field }) => (
                            <TextField
                              {...field}
                              id="outlined-basic"
                              label="Descuento (%)"
                              variant="outlined"
                              value={values.discount}
                              type="number"
                              inputProps={{
                                min: 0,
                                max: 35,
                                step: 1
                              }}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Solo permitir números enteros positivos menores a 36
                                if (value === '' || (Number.isInteger(Number(value)) && Number(value) >= 0 && Number(value) < 36)) {
                                  handleChange(e);
                                }
                              }}
                              helperText="Máximo 35%"
                              fullWidth
                            />
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12} sm={6} fullWidth>
                        <Field name="totalPrice">
                          {({ field }) => (
                            <TextField {...field} id="outlined-basic" label="Precio total" variant="outlined" disabled
                              value={`RD$ ${formatCurrency(safeParseFloat(values.comboPrice || 0) * ((100 - safeParseFloat(values.discount || 0)) / 100))}`} />
                          )}
                        </Field>
                        <ErrorMessage name='totalPrice' component={ErrorMessageStyled} />
                      </Grid>
                      <Grid item xs={12}>
                        <Field name="notes">
                          {({ field }) => (
                            <TextField
                              {...field}
                              id="outlined-basic"
                              label="Notas (opcional)"
                              placeholder='Escribe tus notas u observaciones'
                              variant="outlined"
                              multiline
                              rows={4}
                              fullWidth
                              value={values.notes}
                              onChange={handleChange}
                            />
                          )}
                        </Field>
                      </Grid>
                    </Grid>
                  </Box>
                </fieldset>
                <ErrorMessage name='isSubmitting' component={ErrorMessageStyled} />
                <Box className='reservation-step-btn-container' sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: reservationStep === 0 ? 'flex-end' : 'space-evenly',
                  alignItems: 'center',
                  width: '100%',
                  mt: 2
                }}>
                  <Button variant='contained' onClick={(e) => nextStepHandler(e)} sx={{ fontWeight: 700, fontSize: { xs: '1rem', md: '1.1rem' }, mb: 0.5 }}>
                    Siguiente
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Box>
    </Box>
  );
});

export default EventFormBoda;